/**
 * Garante preenchimento e movimento contínuo da faixa horizontal.
 * O componente mede o primeiro grupo e cria apenas cópias inacessíveis.
 */
if (!customElements.get('horizontal-ticker')) {
  class HorizontalTicker extends HTMLElement {
    /** Inicializa a faixa após fontes e imagens definirem suas dimensões finais. */
    connectedCallback() {
      this.viewport = this.querySelector('.horizontal-ticker__viewport');
      this.track = this.querySelector('[data-ticker-track]');
      this.sourceGroup = this.querySelector('[data-ticker-group]');
      this.handleResize = this.handleResize.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);

      // Uma seção sem blocos completos não possui dimensões confiáveis para animar.
      if (!this.viewport || !this.track || !this.sourceGroup) return;

      this.addEventListener('pointerdown', this.handlePointerDown);
      this.addEventListener('pointerup', this.handlePointerUp);
      this.addEventListener('pointercancel', this.handlePointerUp);
      this.addEventListener('keydown', this.handleKeyDown);

      // Android WebViews antigos podem não possuir ResizeObserver.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.viewport);
      }
      else {
        window.addEventListener('resize', this.handleResize);
      }

      this.waitForContent().then(() => this.rebuild()).catch(() => this.rebuild());
    }

    /** Remove listeners, observadores e o quadro pendente em recargas do editor. */
    disconnectedCallback() {
      this.removeEventListener('pointerdown', this.handlePointerDown);
      this.removeEventListener('pointerup', this.handlePointerUp);
      this.removeEventListener('pointercancel', this.handlePointerUp);
      this.removeEventListener('keydown', this.handleKeyDown);
      window.removeEventListener('resize', this.handleResize);

      // O observador existe somente nos navegadores que oferecem a API.
      if (this.resizeObserver) this.resizeObserver.disconnect();

      // O quadro pode não existir quando a seção foi removida antes da primeira medição.
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
    }

    /** Aguarda fontes e imagens para impedir saltos na distância calculada. */
    async waitForContent() {
      // Navegadores antigos podem não expor a API de fontes; nesse caso, seguimos normalmente.
      if (document.fonts && document.fonts.ready) await document.fonts.ready;

      const images = Array.from(this.querySelectorAll('img'));
      await Promise.all(images.map((image) => {
        // Imagens já carregadas não precisam registrar outro evento.
        if (image.complete) return Promise.resolve();
        return new Promise((resolve) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        });
      }));
    }

    /** Agrupa várias mudanças de largura em uma única reconstrução por quadro. */
    handleResize() {
      // Um quadro pendente já representa a medição mais recente necessária.
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.rebuild();
      });
    }

    /** Alterna a pausa por toque sem interferir em cliques de links. */
    handlePointerDown(event) {
      // Mouse já é atendido por :hover; no toque, a pausa dura somente enquanto há contato.
      if (this.dataset.pause !== 'true' || event.pointerType !== 'touch') return;
      this.classList.add('is-touch-active');
    }

    /** Retoma a animação depois que o toque termina ou é cancelado pela rolagem vertical. */
    handlePointerUp(event) {
      // Outros tipos de ponteiro não controlam a pausa móvel.
      if (event.pointerType !== 'touch') return;
      this.classList.remove('is-touch-active');
    }

    /** Permite pausar e continuar pelo teclado quando a seção é focalizável. */
    handleKeyDown(event) {
      // Somente Espaço e Enter representam a ação de pausa da própria faixa.
      if (this.dataset.pause !== 'true' || ![' ', 'Enter'].includes(event.key)) return;
      event.preventDefault();
      this.classList.toggle('is-keyboard-paused');
    }

    /** Reconstrói cópias suficientes e calcula distância e duração exatas. */
    rebuild() {
      // A limpeza acontece antes da leitura para nunca reutilizar cópias de uma medição anterior.
      this.track.querySelectorAll('[data-ticker-generated-group]').forEach((group) => group.remove());
      Array.from(this.sourceGroup.children)
        .filter((item) => item.hasAttribute('data-ticker-generated-item'))
        .forEach((item) => item.remove());

      // A leitura por children evita dependência do seletor :scope em Android WebViews antigos.
      const sourceItems = Array.from(this.sourceGroup.children)
        .filter((item) => item.hasAttribute('data-ticker-source-item'));

      // Sem itens originais, qualquer duplicação produziria somente marcação vazia.
      if (sourceItems.length === 0) return;

      const targetWidth = this.viewport.clientWidth + this.sourceGroup.getBoundingClientRect().width;
      let safetyCounter = 0;

      // Repete a sequência até que um grupo sozinho cubra a viewport e sua própria transição.
      while (this.sourceGroup.getBoundingClientRect().width < targetWidth && safetyCounter < 20) {
        sourceItems.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.setAttribute('data-ticker-generated-item', '');
          this.makeCloneInaccessible(clone);
          this.sourceGroup.appendChild(clone);
        });
        safetyCounter += 1;
      }

      const generatedGroup = this.sourceGroup.cloneNode(true);
      generatedGroup.removeAttribute('data-ticker-group');
      generatedGroup.setAttribute('data-ticker-generated-group', '');
      this.makeCloneInaccessible(generatedGroup);
      this.track.appendChild(generatedGroup);

      const trackStyles = getComputedStyle(this.track);
      const groupGap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
      const distance = this.sourceGroup.getBoundingClientRect().width + groupGap;
      const configuredSpeed = Number.parseInt(this.dataset.speed, 10) || 4;
      const pixelsPerSecond = 24 + (configuredSpeed * 14);
      const duration = Math.max(distance / pixelsPerSecond, 4);

      this.style.setProperty('--ticker-distance', `${distance}px`);
      this.style.setProperty('--ticker-translate-distance', `${-distance}px`);
      this.style.setProperty('--ticker-duration', `${duration}s`);
      this.restartAnimation();
    }

    /** Força o navegador a reavaliar os keyframes depois que as distâncias foram medidas. */
    restartAnimation() {
      // Decisão de produto: este ticker permanece animado mesmo com redução de movimento ativa no sistema.

      this.track.style.animationName = 'none';
      void this.track.offsetWidth;
      this.track.style.removeProperty('animation-name');
    }

    /** Remove foco, IDs e atributos do editor das cópias visuais. */
    makeCloneInaccessible(clone) {
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((element) => element.removeAttribute('data-shopify-editor-block'));
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => element.setAttribute('tabindex', '-1'));
    }
  }

  customElements.define('horizontal-ticker', HorizontalTicker);
}
